import { supabase } from '@/lib/supabase'
import type { Affiliate, ListAffiliatesParams, ListAffiliatesResult } from '../types'
import { mapFromAffiliatesRow, mapFromProfilesRow } from '../utils/mappers'

export async function listAffiliates({ search = '', page = 1, pageSize = 25 }: ListAffiliatesParams): Promise<ListAffiliatesResult> {
  const from = (page - 1) * pageSize
  const to = from + pageSize - 1

  // Strategy:
  // 1) Try dedicated affiliates table (recommended model)
  // 2) Fallback to profiles where role = 'afiliado'
  try {
    let base = supabase.from('affiliates')
    // Request only stable columns to avoid errors if no view/relationship is set
    let query = base.select('user_id, created_at, active, code', { count: 'exact' })
    if (search) {
      const s = `%${search}%`
      // Without join we can only filter by code; leave name/email to fallback strategy
      query = query.ilike('code', s)
    }
    query = query.order('created_at', { ascending: false }).range(from, to)
    const { data, error, count } = await query
    if (error) throw error

    const baseItems = (data ?? []).map(mapFromAffiliatesRow)
    // Enrich with profile name/email via secured master view
    const missing = baseItems.filter((i) => !i.name || !i.email)
    if (missing.length > 0) {
      const ids = Array.from(new Set(missing.map((m) => m.id)))
      const { data: profs } = await supabase
        .from('v_profiles_contact_master')
        .select('id, full_name, email')
        .in('id', ids)
      const byId = new Map((profs ?? []).map((p: any) => [p.id, p]))
      for (const item of baseItems) {
        if (!item.name || !item.email) {
          const p = byId.get(item.id)
          if (p) {
            item.name = item.name ?? p.full_name ?? null
            item.email = item.email ?? p.email ?? null
          }
        }
      }
    }

    return { items: baseItems, total: count ?? 0 }
  } catch (e: any) {
    // If table doesn't exist or permission denied, fallback to profiles
    // Postgres undefined table: code '42P01'
    // We'll fallback for any error to keep UI resilient in early phases
  }

  let query = supabase
    .from('v_profiles_contact_master')
    .select('id, full_name, email, created_at, role', { count: 'exact' })
    .eq('role', 'afiliado')

  if (search) {
    const s = `%${search}%`
    query = query.or(`full_name.ilike.${s},email.ilike.${s}`)
  }

  query = query.order('created_at', { ascending: false }).range(from, to)
  const { data, error, count } = await query
  if (error) throw error
  const items = (data ?? []).map(mapFromProfilesRow)
  return { items, total: count ?? 0 }
}

export type CreateAffiliatePayload = {
  name: string
  email: string
  code: string
  commissionMode: 'percent' | 'fixed'
  commissionValue: number
  recurrence: 'one_time' | 'recurring'
  parentAffiliateId?: string | null
  uplineCommissionPercent?: number // 0-100
  coupon?: {
    code: string
    mode: 'percent' | 'fixed'
    value: number
    recurrence: 'one_time' | 'recurring'
    active?: boolean
  }
}

export async function createAffiliateByEmail(payload: CreateAffiliatePayload): Promise<void> {
  const emailNorm = payload.email.trim().toLowerCase()
  // Resolve user id from secure view (master-only)
  const { data: users, error: findErr } = await supabase
    .from('v_profiles_contact_master')
    .select('id, email, full_name')
    .eq('email', emailNorm)
    .limit(1)
  if (findErr) throw findErr
  const u = (users ?? [])[0]
  if (!u) {
    const { error: fnErr } = await supabase.functions.invoke('admin-create-affiliate', {
      body: {
        email: payload.email,
        name: payload.name,
        code: payload.code,
        commissionMode: payload.commissionMode,
        commissionValue: payload.commissionValue,
        recurrence: payload.recurrence,
        parentAffiliateId: payload.parentAffiliateId ?? null,
        uplineCommissionPercent: payload.uplineCommissionPercent ?? 0,
        coupon: payload.coupon,
      },
    })
    if (fnErr) throw fnErr
    return
  }

  // Create affiliate if not exists
  const { error: upsertAffErr } = await supabase
    .from('affiliates')
    .upsert({
      user_id: u.id,
      code: payload.code,
      active: true,
      parent_affiliate_id: payload.parentAffiliateId ?? null,
      upline_commission_mode: 'percent',
      upline_commission_value: (payload.uplineCommissionPercent ?? 0) / 100,
    }, { onConflict: 'user_id' })
  if (upsertAffErr) throw upsertAffErr

  // Upsert commission settings
  const value = payload.commissionMode === 'percent' ? payload.commissionValue / 100 : payload.commissionValue
  const { error: setErr } = await supabase
    .from('affiliate_settings')
    .upsert({
      affiliate_user_id: u.id,
      commission_mode: payload.commissionMode,
      commission_value: value,
      recurrence: payload.recurrence,
    })
  if (setErr) throw setErr

  if (payload.coupon && payload.coupon.code.trim()) {
    const v = payload.coupon.mode === 'percent' ? payload.coupon.value / 100 : payload.coupon.value
    const { error: coupErr } = await supabase
      .from('affiliate_coupons')
      .upsert({
        affiliate_user_id: u.id,
        code: payload.coupon.code.trim(),
        mode: payload.coupon.mode,
        value: v,
        recurrence: payload.coupon.recurrence,
        active: payload.coupon.active ?? true,
      }, { onConflict: 'code' })
    if (coupErr) throw coupErr
  }
}

export async function isAffiliateCodeAvailable(code: string): Promise<boolean> {
  const c = code.trim()
  if (!c) return false
  const { data, error } = await supabase.from('affiliates').select('code').eq('code', c).limit(1)
  if (error) throw error
  return (data ?? []).length === 0
}

export async function isCouponCodeAvailable(code: string): Promise<boolean> {
  const c = code.trim()
  if (!c) return false
  const { data, error } = await supabase.from('affiliate_coupons').select('code').eq('code', c).limit(1)
  if (error) throw error
  return (data ?? []).length === 0
}

export type AffiliateDetails = {
  userId: string
  name: string | null
  email: string | null
  code: string | null
  commissionMode: 'percent' | 'fixed'
  commissionValue: number
  recurrence: 'one_time' | 'recurring'
  parentAffiliateId: string | null
  uplineCommissionPercent: number
  coupon?: {
    id?: string
    code: string
    mode: 'percent' | 'fixed'
    value: number
    recurrence: 'one_time' | 'recurring'
    active: boolean
  }
}

export async function getAffiliateDetails(userId: string): Promise<AffiliateDetails> {
  // base info
  const { data: base } = await supabase.from('v_profiles_contact_master').select('id, full_name, email').eq('id', userId).maybeSingle()
  const { data: aff } = await supabase
    .from('affiliates')
    .select('user_id, code, parent_affiliate_id, upline_commission_mode, upline_commission_value')
    .eq('user_id', userId)
    .maybeSingle()
  const { data: set } = await supabase.from('affiliate_settings').select('commission_mode, commission_value, recurrence').eq('affiliate_user_id', userId).maybeSingle()
  const { data: coups } = await supabase
    .from('affiliate_coupons')
    .select('id, code, mode, value, recurrence, active')
    .eq('affiliate_user_id', userId)
    .order('created_at', { ascending: false })
    .limit(1)
  const coup = (coups ?? [])[0]

  const mode = (set?.commission_mode as 'percent'|'fixed') || 'percent'
  const val = Number(set?.commission_value || 0)
  const uplineMode = (aff as any)?.upline_commission_mode || 'percent'
  const uplineVal = Number((aff as any)?.upline_commission_value ?? 0)
  const uplinePercent = uplineMode === 'percent' ? uplineVal * 100 : 0

  return {
    userId,
    name: (base as any)?.full_name ?? null,
    email: (base as any)?.email ?? null,
    code: aff?.code ?? null,
    commissionMode: mode,
    commissionValue: mode === 'percent' ? val * 100 : val,
    recurrence: (set?.recurrence as any) || 'one_time',
    parentAffiliateId: (aff as any)?.parent_affiliate_id ?? null,
    uplineCommissionPercent: uplinePercent,
    coupon: coup
      ? {
          id: coup.id,
          code: coup.code,
          mode: coup.mode,
          value: coup.mode === 'percent' ? Number(coup.value || 0) * 100 : Number(coup.value || 0),
          recurrence: coup.recurrence,
          active: !!coup.active,
        }
      : undefined,
  }
}

export async function updateAffiliateById(userId: string, payload: Omit<CreateAffiliatePayload, 'email' | 'name'>): Promise<void> {
  // upsert affiliate code
  const { error: affErr } = await supabase
    .from('affiliates')
    .upsert({
      user_id: userId,
      code: payload.code,
      active: true,
      parent_affiliate_id: payload.parentAffiliateId ?? null,
      upline_commission_mode: 'percent',
      upline_commission_value: (payload.uplineCommissionPercent ?? 0) / 100,
    }, { onConflict: 'user_id' })
  if (affErr) throw affErr

  // settings
  const value = payload.commissionMode === 'percent' ? payload.commissionValue / 100 : payload.commissionValue
  const { error: setErr } = await supabase
    .from('affiliate_settings')
    .upsert({
      affiliate_user_id: userId,
      commission_mode: payload.commissionMode,
      commission_value: value,
      recurrence: payload.recurrence,
    })
  if (setErr) throw setErr

  if (payload.coupon && payload.coupon.code.trim()) {
    const v = payload.coupon.mode === 'percent' ? payload.coupon.value / 100 : payload.coupon.value
    const { error: coupErr } = await supabase
      .from('affiliate_coupons')
      .upsert({
        affiliate_user_id: userId,
        code: payload.coupon.code.trim(),
        mode: payload.coupon.mode,
        value: v,
        recurrence: payload.coupon.recurrence,
        active: payload.coupon.active ?? true,
      }, { onConflict: 'code' })
    if (coupErr) throw coupErr
  }
}

export async function suspendAffiliate(userId: string): Promise<void> {
  const { error } = await supabase
    .from('affiliates')
    .update({ active: false })
    .eq('user_id', userId)
  if (error) throw error
}

export async function activateAffiliate(userId: string): Promise<void> {
  const { error } = await supabase
    .from('affiliates')
    .update({ active: true })
    .eq('user_id', userId)
  if (error) throw error
}

export async function deleteAffiliate(userId: string): Promise<void> {
  const { error } = await supabase.functions.invoke('admin-delete-affiliate', { body: { userId } })
  if (error) throw error
}
